import { request2 } from '@/utils/httpClient'
import { getAuthHeaders } from './requestHelpers'

export const createRender = (promptName, idempotencyKey) => {
  const formData = new FormData()
  formData.append('prompt_name', promptName)
  formData.append('idempotency_key', idempotencyKey)

  return request2({
    url: '/MakeHouse/create',
    method: 'POST',
    headers: getAuthHeaders(),
    data: formData
  })
}

/**
 * 建筑渲染设计 创建设计信息接口 (/MakeHouse/aiDesignInfo)
 * @param {Object} params - 设计信息参数
 * @param {string} params.id - 项目ID
 * @param {File} params.housePicture - 房屋图片文件
 * @param {File} params.backgroundPicture - 背景图片文件
 * @param {string} params.timePeriod - 时间周期 (morning/afternoon/evening/night)
 * @param {string} params.userRequest - 用户设计要求
 * @param {number} params.maxImages - 输出图片数量 (1/3/5)
 * @param {string} params.aspectRatio - 输出图片比例 (如 "1:1", "16:9")，用于后端落库 aspect_ratio
 * @param {string|number} [params.orderId] - 订单ID（可选）
 * @param {string} [params.size] - 输出像素尺寸（如 "2560x1440"），用于后端 size
 * @param {string} [params.resolution] - 输出分辨率档位（如 "2K"）
 * @param {string} [params.angle] - 视角
 * @param {string} [params.backgroundName] - 预设背景名
 * @param {string} [params.backgroundType] - 背景类型
 * @param {string} [params.wallMaterial] - 外墙材质
 * @param {string} [params.wallWidth] - 外墙宽度
 * @param {string} [params.wallDirection] - 外墙方向
 * @param {string} [params.wallColor] - 外墙颜色
 * @param {File}   [params.wallImage] - 外墙参考图
 * @param {string} [params.windowMaterial] - 窗框材质
 * @param {string} [params.windowColor] - 窗框颜色
 * @param {File}   [params.houseMask] - 房屋mask（可选）
 * @param {File}   [params.backgroundMask] - 背景mask（可选）
 * @param {string} [params.houseCanvasJson] - 房屋画板 fabric JSON（可选）
 * @param {string} [params.bgCanvasJson] - 背景画板 fabric JSON（可选）
 * @param {string} [params.mediaType] - 媒体类型（可选，VIDEO 表示视频任务）
 * @param {string} [params.interiorImageSource] - interior 输入来源（floorplan/interior_image）
 * @param {boolean|string|number} [params.precisePromptEnabled] - 精准提示词开关（仅透传后端）
 * @param {string[]} [params.facadeMaskColors] - facade 画板使用颜色并集（house+bg）
 * @param {string} [params.interiorStylizedImage] - 内装画面风格：sketch / watercolor / realism
 * @param {string} [params.interiorStyle] - 内装风格（japandi/natural/simple_modern/vintage/empty/custom）
 * @param {File}   [params.styleImage] - 自定义风格参考图 PNG（内装 custom / 立面画板，互斥共用 style_image）
 * @param {string} [params.styleCanvasJson] - 画板 fabric JSON（图 src 为 __INTERIOR_CANVAS_ASSET_n__ 占位符）
 * @param {string} [params.styleCanvasName] - 画板名称
 * @param {File[]} [params.styleCanvasAssets] - 画板素材文件，顺序与占位符下标一致
 * @param {number} [params.styleCanvasId] - アカウントの画板の id（saveStyleCanvas で保存済み）。あれば styleCanvasJson は送らなくてよい
 * @param {File|File[]} params.descriptivePictures - 描述图片文件（单个或数组，最多3个）
 * @returns {Promise} - 请求Promise对象
 */

export const designRender = (params) => {
  // 创建FormData对象处理文件上传
  const formData = new FormData()
  formData.append('id', params.id)
  formData.append('house_picture', params.housePicture)
  formData.append('background_picture', params.backgroundPicture)
  // 内装“スタイル”按钮暂时隐藏时会传空值；后续恢复按钮后会继续正常透传 time_period。
  if (params.timePeriod !== undefined && params.timePeriod !== null && params.timePeriod !== '') {
    formData.append('time_period', params.timePeriod)
  }
  formData.append('user_request', params.userRequest)
  // 添加订单号
  if (params.orderId) {
    formData.append('order_id', String(params.orderId))
  }
  // 添加设计类别
  if (params.interiorImageSource) {
    formData.append('interior_image_source', String(params.interiorImageSource))
  }
  // 提示詞の切り替え（3D エディタの俯瞰カメラ = model_overhead）
  if (params.interiorInputVariant) {
    formData.append('interior_input_variant', String(params.interiorInputVariant))
  }
  //添加内装设计图片风格
  if (params.interiorStylizedImage) {
    formData.append('interior_stylized_image', String(params.interiorStylizedImage))
  }

  // 添加图片生成数量参数
  if (params.maxImages) {
    formData.append('max_images', String(params.maxImages))
  }
  
  // 添加分辨率参数
  if (params.resolution) {
    formData.append('resolution', params.resolution)
  }

  // 添加视角参数
  if (params.angle) {
    formData.append('angle', params.angle)
  }

  // 添加像素尺寸参数
  if (params.size) {
    formData.append('size', params.size)
  }

  // 添加长宽比参数
  if (params.aspectRatio) {
    formData.append('aspect_ratio', params.aspectRatio)
  }

  if (params.backgroundName) {
    formData.append('background_name', params.backgroundName)
  }

  if (params.backgroundType) {
    formData.append('background_type', params.backgroundType)
  }

  if (params.wallMaterial) {
    formData.append('wall_material', params.wallMaterial)
  }
  if (params.wallWidth) {
    formData.append('wall_width', params.wallWidth)
  }
  if (params.wallDirection) {
    formData.append('wall_direction', params.wallDirection)
  }
  if (params.wallColor) {
    formData.append('wall_color', params.wallColor)
  }
  if (params.wallImage) {
    formData.append('wall_image', params.wallImage)
  }
  if (params.houseMask) {
    formData.append('house_mask', params.houseMask)
  }
  if (params.backgroundMask) {
    formData.append('background_mask', params.backgroundMask)
  }
  if (params.houseCanvasJson) {
    formData.append('house_canvas_json', params.houseCanvasJson)
  }
  if (params.bgCanvasJson) {
    formData.append('bg_canvas_json', params.bgCanvasJson)
  }
  if (params.windowMaterial) {
    formData.append('window_material', params.windowMaterial)
  }
  if (params.windowColor) {
    formData.append('window_color', params.windowColor)
  }
  if (params.mediaType) {
    formData.append('media_type', params.mediaType)
  }
  if (params.precisePromptEnabled !== undefined && params.precisePromptEnabled !== null) {
    formData.append('precise_prompt_enabled', String(params.precisePromptEnabled))
  }
  if (Array.isArray(params.facadeMaskColors)) {
    formData.append('facade_mask_colors', JSON.stringify(params.facadeMaskColors))
  }
  if (params.interiorStyle) {
    formData.append('interior_style', params.interiorStyle)
  }
  if (params.styleImage) {
    formData.append('style_image', params.styleImage)
  }
  if (params.styleCanvasJson) {
    formData.append('style_canvas_json', String(params.styleCanvasJson))
  }
  if (params.styleCanvasName) {
    formData.append('style_canvas_name', String(params.styleCanvasName))
  }
  if (params.styleCanvasId) {
    formData.append('style_canvas_id', String(params.styleCanvasId))
  }
  if (params.styleCanvasAssets && Array.isArray(params.styleCanvasAssets)) {
    params.styleCanvasAssets.forEach((f) => {
      if (f) formData.append('style_canvas_asset', f)
    })
  }

  // 添加描述图片（支持多图，最多3张）
  if (params.descriptivePictures) {
    if (Array.isArray(params.descriptivePictures)) {
      // 多个文件
      params.descriptivePictures.slice(0, 3).forEach(file => {
        if (file) formData.append('descriptive_picture_path', file)
      })
    } else {
      // 单个文件
      if (params.descriptivePictures) formData.append('descriptive_picture_path', params.descriptivePictures)
    }
  }

  return request2({
    url: '/MakeHouse/aiDesignInfo',
    method: 'POST',
    headers: getAuthHeaders(),
    data: formData
  })
}

/**
 *建筑渲染设计获取设计结果接口 (/MakeHouse/aiDesignResult)
 * @param {Object} params - 查询参数
 * @param {string} params.id - 项目ID
 * @param {string} params.projectName - 项目名称
 * @param {string} params.attemptId - 尝试ID
 * @returns {Promise} - 请求Promise对象
 */

export const getResultRender = (params) => {
  return request2({
    url: '/MakeHouse/aiDesignResult',
    method: 'GET',
    headers: getAuthHeaders(),
    params: {
      id: params.id,
      projectName: params.projectName,
      attemptId: params.attemptId
    }
  })
}

/**
 * 建筑渲染设计 获取单个项目接口 (/MakeHouse/findOne)
 * @param {string} id - 项目ID
 * @returns {Promise} - 请求Promise对象
 */

export const findRender = (id) => {
  return request2({
    url: '/MakeHouse/findOne',
    method: 'GET',
    headers: getAuthHeaders(),
    params: {
      id: id
    }
  })
}

/**
 * 删除账号下某个画板 JSON（后端会清空 style_canvas_json，内装/立面共用）
 * @param {string} canvasJson
 * @returns {Promise}
 */

export const deleteStyleCanvasJson = (canvasJson) => {
  return request2({
    url: '/MakeHouse/deleteStyleCanvasJson',
    method: 'POST',
    headers: getAuthHeaders(),
    params: {
      canvas_json: canvasJson
    }
  })
}

/**
 * 更新账号下某个画板名称
 * @param {string} canvasJson
 * @param {string} name
 * @returns {Promise}
 */

/**
 * アカウントの画板を保存する（編集画面の「保存」）。id があればその画板を書き換え、無ければ新しく作る。
 * 以前は出図したときだけ保存していたため、出図前の画板は開き直すと消えていた。
 * @param {{ id?: number, kind: 'facade'|'interior', name?: string, canvasJson: string, assets?: File[], styleImage?: File }} params
 *   canvasJson は画像を __INTERIOR_CANVAS_ASSET_n__ に置き換えたもの（prepareInteriorCanvasUpload）
 * @returns {Promise} data: { id, kind, name, canvasJson（URL）, styleImage（URL） }
 */
export const saveStyleCanvas = (params) => {
  const formData = new FormData()
  if (params.id) formData.append('id', String(params.id))
  formData.append('kind', params.kind === 'interior' ? 'interior' : 'facade')
  if (params.name) formData.append('name', String(params.name))
  formData.append('canvas_json', String(params.canvasJson))
  ;(params.assets || []).forEach((f) => {
    if (f) formData.append('style_canvas_asset', f)
  })
  if (params.styleImage) formData.append('style_image', params.styleImage)
  return request2({
    url: '/MakeHouse/styleCanvas/save',
    method: 'POST',
    headers: getAuthHeaders(),
    data: formData
  })
}

/** アカウントの画板の名前を変える（id で指定） */
export const renameStyleCanvas = (id, name) => {
  return request2({
    url: '/MakeHouse/styleCanvas/rename',
    method: 'POST',
    headers: getAuthHeaders(),
    params: { id, name }
  })
}

/** アカウントの画板を消す（id で指定） */
export const deleteStyleCanvas = (id) => {
  return request2({
    url: '/MakeHouse/styleCanvas/delete',
    method: 'POST',
    headers: getAuthHeaders(),
    params: { id }
  })
}

export const updateStyleCanvasName = (canvasJson, name) => {
  return request2({
    url: '/MakeHouse/updateStyleCanvasName',
    method: 'POST',
    headers: getAuthHeaders(),
    params: {
      canvas_json: canvasJson,
      name
    }
  })
}

/**
 * 查询项目结果图片的收藏状态
 * @param {number|string} projectId - 项目ID
 * @returns {Promise}
 */

export const getProjectFavoriteStatuses = (projectId) => {
  return request2({
    url: '/api/result-images/project-favorites',
    method: 'GET',
    headers: getAuthHeaders(),
    params: {
      projectId: projectId
    }
  })
}

/**
 * 设置图片为收藏
 * @param {string} imageUrl - 图片URL
 * @param {number} projectId - 项目ID
 * @returns {Promise} - 请求Promise对象
 */

export const addFavorite = (imageUrl, projectId) => {
  const formData = new FormData()
  formData.append('imageUrl', imageUrl)
  formData.append('projectId', String(projectId))
  
  return request2({
    url: '/api/result-images/favorite',
    method: 'POST',
    headers: getAuthHeaders(),
    data: formData
  })
}

/**
 * 取消图片收藏
 * @param {string} imageUrl - 图片URL
 * @param {number} projectId - 项目ID
 * @returns {Promise} - 请求Promise对象
 */

export const removeFavorite = (imageUrl, projectId) => {
  const formData = new FormData()
  formData.append('imageUrl', imageUrl)
  formData.append('projectId', String(projectId))
  
  return request2({
    url: '/api/result-images/unfavorite',
    method: 'POST',
    headers: getAuthHeaders(),
    data: formData
  })
}

/**
 * 获取用户所有收藏的图片
 * @returns {Promise} - 请求Promise对象，返回 { imageUrls: string[], total: number }
 */

export const getUserFavorites = () => {
  return request2({
    url: '/api/result-images/user-favorites',
    method: 'GET',
    headers: getAuthHeaders()
  })
}

/**
 * 提交用户渲染结果反馈
 * @param {string} imageUrl - 结果图片原始存储路径或URL
 * @param {string} problemDescription - 问题描述
 * @returns {Promise}
 */

export const submitUserFeedback = (imageUrl, problemDescription) => {
  const formData = new FormData()
  formData.append('imageUrl', imageUrl)
  formData.append('problemDescription', problemDescription)

  return request2({
    url: '/api/user-feedback/submit',
    method: 'POST',
    headers: getAuthHeaders(),
    data: formData
  })
}

/**
 * 建筑渲染设计 项目列表接口 (/MakeHouse/list)
 * @param {Object} params - 分页和搜索参数
 * @param {number} params.pageNum - 页码
 * @param {number} params.pageSize - 每页条数
 * @param {string} [params.content] - 搜索内容（可选）
 * @returns {Promise} - 请求Promise对象
 */

export const listRender = (params) => {
  return request2({
    url: '/MakeHouse/list',
    method: 'GET',
    headers: getAuthHeaders(),
    params: {
      pageNum: params.pageNum,
      pageSize: params.pageSize,
      content: params.content || '' // 默认为空字符串
    }
  })
}

/**
 *建筑渲染设计 删除项目接口 (/MakeHouse/delete)
 * @param {string} id - 项目ID
 * @returns {Promise} - 请求Promise对象
 */

export const deleteRender = (id) => {
  const formData = new FormData()
  formData.append('id', String(id))

  return request2({
    url: '/MakeHouse/delete',
    method: 'POST',
    headers: getAuthHeaders(),
    data: formData
  })
}

/**
 * 建筑渲染设计 删除单张生成结果图 (/MakeHouse/deleteResultImage)
 * 仅项目所有者可删；后端会同时清理该图的收藏记录
 * @param {string|number} id - 项目ID
 * @param {string} imageUrl - 结果图原始路径或完整下载 URL
 * @returns {Promise}
 */
export const deleteResultImage = (id, imageUrl) => {
  const formData = new FormData()
  formData.append('id', String(id))
  formData.append('image_url', String(imageUrl))

  return request2({
    url: '/MakeHouse/deleteResultImage',
    method: 'POST',
    headers: getAuthHeaders(),
    data: formData
  })
}

// 获取渲染等待视频 URL（用户侧，设计等待页面播放用，任意已登录用户可访问）

export const getRenderLoadingVideoUrl = () => {
  return request2({
    url: '/video/homepage-url',
    method: 'GET',
    headers: getAuthHeaders()
  })
}

// ==== AI Real CG 单点使用锁（同账号同一时刻仅一台设备可使用）====

export const acquireAirenderSession = (deviceId) => request2({
  url: '/api/airender-session/acquire',
  method: 'POST',
  headers: getAuthHeaders(),
  params: { deviceId }
})

export const heartbeatAirenderSession = (deviceId) => request2({
  url: '/api/airender-session/heartbeat',
  method: 'POST',
  headers: getAuthHeaders(),
  params: { deviceId }
})

export const releaseAirenderSession = (deviceId) => request2({
  url: '/api/airender-session/release',
  method: 'POST',
  headers: getAuthHeaders(),
  params: { deviceId }
})

export const takeoverAirenderSession = (deviceId) => request2({
  url: '/api/airender-session/takeover',
  method: 'POST',
  headers: getAuthHeaders(),
  params: { deviceId }
})

/**
 * 生成結果に写っている「動かせるもの」を一覧で取る (/MakeHouse/objectScan)
 *
 * 非同期。返る attempt_id で `getResultRender` を叩いて完了を待つ（実測 20 秒前後）。
 *
 * @param {Object} params
 * @param {number} params.id - 案件ID
 * @param {string} params.renderResultPath - 対象の結果画像の OSS パス
 * @param {string} [params.scene] - interior（部屋の中）/ facade（建物と外構）
 * @returns {Promise}
 */
export const createObjectScan = (params) => {
  const formData = new FormData()
  formData.append('id', params.id)
  formData.append('render_result_path', params.renderResultPath)
  if (params.scene) formData.append('scene', params.scene)

  return request2({
    url: '/MakeHouse/objectScan',
    method: 'POST',
    headers: getAuthHeaders(),
    data: formData
  })
}

/**
 * 選ばれた家具を、回せる 3D と「その家具が無い背景」に分ける (/MakeHouse/objectRotate)
 *
 * 非同期。返る attempt_id で `getResultRender` を叩いて完了を待つ（実測 35〜120 秒）。
 *
 * @param {Object} params
 * @param {number} params.id - 案件ID
 * @param {string} params.renderResultPath - 対象の結果画像の OSS パス
 * @param {Array<Array<Array<number>>>} params.outlines - 選ばれた対象。1要素が1つの対象で、
 *   その中に y1,x1,y2,x2,... の数列が輪郭の数だけ入る（重なっている家具は1つの対象）
 * @param {string} [params.label] - 対象の呼び名
 * @returns {Promise}
 */
export const createObjectRotate = (params) => {
  const formData = new FormData()
  formData.append('id', params.id)
  formData.append('render_result_path', params.renderResultPath)
  formData.append('outlines', JSON.stringify(params.outlines || []))
  if (params.label) formData.append('label', params.label)
  // 塊ごとの呼び名。outlines と同じ並び。去背の指示にその塊の名前だけを使わせる
  if (params.labels) formData.append('labels', JSON.stringify(params.labels))
  // 3D 化する塊の番号（outlines の並び）。無ければ全部。他の塊は背景から消して
  // 切り抜きだけが返る
  if (params.splatIndices) formData.append('splat_indices', JSON.stringify(params.splatIndices))
  // false のとき、同じ画像・同じ輪郭の作り置き（背景板・切り抜き層）を使わず作り直す。
  // 既定は使い回す（塊の選び直しは splat 1 回分の待ちで済む）
  if (params.reuseLayers === false) formData.append('reuse_layers', 'false')

  return request2({
    url: '/MakeHouse/objectRotate',
    method: 'POST',
    headers: getAuthHeaders(),
    data: formData
  })
}
